import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ClassNames from "classnames";
import "./TreeNode.scss";
import Highlighter from "react-highlight-words";
import {
  CHILDREN_PAGE_SIZE,
  getIconByType,
  TristateStateEnum,
  type TristateState,
} from "../TreeUtils";
import { resourceTypes } from "../../../Findings/FindingsUtils";
import {
  CheckBox,
  Tooltip,
  IconButton,
  TextButton,
  formatNumber,
} from "@SeemplicityDev/shared";
// `TreeNode` is also the name of this component, so the shared node interface
// is aliased to avoid the collision.
import type { TreeActionTooltips, TreeNode as TreeNodeData } from "../types";

export interface CheckedNode {
  id?: string;
  checked?: TristateState;
}

interface TreeNodeOperations {
  onEdit?: (node: TreeNodeData) => void;
  // Opt-in third row action, rendered between Edit and Delete.
  onMove?: (node: TreeNodeData) => void;
  onSelect?: (node: TreeNodeData) => void;
  onDelete?: (node: TreeNodeData) => void;
  onCheck?: (node: TreeNodeData, checkedNodes?: CheckedNode[]) => void;
  onWarning?: (id?: string) => boolean;
}

export interface TreeNodeProps extends TreeNodeData {
  name?: string;
  id?: string;
  type?: string;
  children?: TreeNodeData[];
  level: number;
  nodeData: TreeNodeData;
  nodeOperations: TreeNodeOperations;
  searchValue?: string;
  parentId?: string | null;
  "data-testid"?: string;
  editableNodes?: boolean;
  selectedNode?: string;
  className?: string;
  isLastChild?: boolean;
  withCheckbox?: boolean;
  checkedNodes?: CheckedNode[];
  initialExpandLevel?: number;
  expandedNodes?: string[];
  isSelectableNode: (node: TreeNodeData) => boolean;
  isHiddenRoot?: boolean;
  readOnly?: boolean;
  warningTooltip?: React.ReactNode;
  getActionTooltips?: (node: TreeNodeData) => TreeActionTooltips;
  scrollToSelected?: boolean;
  getNodeIconByType?: ((type?: string, id?: string) => React.ReactNode) | null;
  getNodeActions?: ((node: TreeNodeData) => React.ReactNode) | null;
  previewMode?: boolean;
  getRightComponent?: ((node: TreeNodeData) => React.ReactNode) | null;
  getExtendedTitle?: ((node: TreeNodeData) => React.ReactNode) | null;
  tooltipContent?: ((node: TreeNodeData) => React.ReactNode) | null;
  isStatic?: boolean;
  isHighlightedNode?: ((node: TreeNodeData) => boolean) | null;
}

// Expansion of a node the user has not toggled themselves.
const getDefaultExpanded = (
  level: number,
  initialExpandLevel: number | undefined,
  searchValue: string | undefined,
  expandedNodes: string[] | undefined,
  id: string | undefined
): boolean => {
  if (searchValue) {
    return true;
  }
  if (expandedNodes?.includes(id ?? "")) {
    return true;
  }
  if (initialExpandLevel) {
    return level < initialExpandLevel;
  }
  return true;
};

interface ManualExpand {
  expanded: boolean;
  searchValue?: string;
}

interface VisibleChildren {
  count: number;
  searchValue?: string;
}

const getShowMoreLabel = (remainingCount: number): string =>
  remainingCount > CHILDREN_PAGE_SIZE
    ? `Show ${formatNumber(CHILDREN_PAGE_SIZE)} more (${formatNumber(
        remainingCount
      )} remaining)`
    : `Show ${formatNumber(remainingCount)} more`;

const TreeNode: React.FC<TreeNodeProps> = ({
  name,
  id,
  type,
  children,
  level,
  nodeData,
  nodeOperations,
  searchValue,
  parentId,
  "data-testid": dataTestId,
  editableNodes,
  selectedNode,
  className,
  isLastChild,
  withCheckbox,
  checkedNodes,
  initialExpandLevel,
  expandedNodes,
  isSelectableNode,
  isHiddenRoot,
  readOnly,
  warningTooltip,
  getActionTooltips,
  scrollToSelected,
  getNodeIconByType = null,
  getNodeActions = null,
  previewMode,
  getRightComponent = null,
  getExtendedTitle = null,
  tooltipContent = null,
  isStatic = false,
  isHighlightedNode = null,
}) => {
  const { onEdit, onMove, onSelect, onDelete } = nodeOperations;
  const nodeActions = getNodeActions?.(nodeData);
  const canEditNode = !!editableNodes && !!nodeData?.editable;
  const actionTooltips = getActionTooltips?.(nodeData) ?? {};
  const isExpandable = (children?.length ?? 0) > 0;
  const warningIcon = (
    <i className={`seem-icon seem-icon-warning node-warning-icon`} />
  );
  const showExpandToggle = isExpandable && !isStatic;
  // The override is keyed to the search term it was made under, so it is
  // dropped as soon as the term changes - including when the search is
  // cleared, in the very render that swaps the data back to the full tree.
  const [manualExpand, setManualExpand] = useState<ManualExpand | null>(null);
  const expand =
    isStatic ||
    (manualExpand && manualExpand.searchValue === searchValue
      ? manualExpand.expanded
      : getDefaultExpanded(
          level,
          initialExpandLevel,
          searchValue,
          expandedNodes,
          id
        ));
  // Paging is keyed to the search term as well, so a re-filtered group starts
  // from its first page.
  const [visibleChildren, setVisibleChildren] =
    useState<VisibleChildren | null>(null);
  const pagedChildrenCount =
    visibleChildren && visibleChildren.searchValue === searchValue
      ? visibleChildren.count
      : CHILDREN_PAGE_SIZE;
  const selectedChildIndex = useMemo(
    () =>
      selectedNode
        ? (children?.findIndex((child) => child.id === selectedNode) ?? -1)
        : -1,
    [children, selectedNode]
  );
  // A selected direct child is kept inside the rendered pages so it stays visible.
  const visibleChildrenCount =
    selectedChildIndex >= pagedChildrenCount
      ? Math.ceil((selectedChildIndex + 1) / CHILDREN_PAGE_SIZE) *
        CHILDREN_PAGE_SIZE
      : pagedChildrenCount;
  const selectedRef = useRef<HTMLDivElement>(null);

  const isNodeChecked = useCallback(
    (id?: string) => {
      return (
        checkedNodes?.find(
          (node) =>
            node?.id === id && node?.checked === TristateStateEnum.CHECKED
        ) !== undefined
      );
    },
    [checkedNodes]
  );

  const isNodeIndeterminate = useCallback(
    (id?: string) => {
      return (
        checkedNodes?.find(
          (node) =>
            node?.id === id && node?.checked === TristateStateEnum.INDETERMINATE
        ) !== undefined
      );
    },
    [checkedNodes]
  );

  const hasWarning = useCallback(
    (id?: string) => {
      if (nodeOperations.onWarning?.(id)) {
        nodeData.hasWarning = true;
        return true;
      }
    },
    [checkedNodes]
  );

  const classNameWrapper = ClassNames({
    "is-expandable": isExpandable,
    "is-expanded": expand,
    "is-collapsed": !expand,
    "is-highlighted": !!isHighlightedNode?.(nodeData),
    "preview-scope":
      previewMode && !nodeData?.already_exists && type === "scope",
    "preview-group":
      previewMode && !nodeData?.already_exists && type === "group",
    "preview-error": previewMode && nodeData?.error,
  });

  const renderChildren = () => {
    if (!isExpandable || !expand) {
      return null;
    }

    const renderedChildren = children?.slice(0, visibleChildrenCount) ?? [];
    const remainingCount = (children?.length ?? 0) - renderedChildren.length;

    return (
      <>
        {renderedChildren.map((node, index) => {
          // The show more row takes over as the last item when one follows.
          const isLast =
            index === renderedChildren.length - 1 && remainingCount === 0;
          return (
            <React.Fragment key={node.id ?? `${level}-${index}`}>
              <TreeNode
                {...node}
                nodeData={node}
                parentId={node.parentId}
                nodeOperations={nodeOperations}
                level={level + 1}
                searchValue={searchValue}
                withCheckbox={withCheckbox}
                data-testid={`tree-node-${node.type}`}
                selectedNode={selectedNode}
                editableNodes={editableNodes}
                checkedNodes={checkedNodes}
                initialExpandLevel={initialExpandLevel}
                className={isLast ? "node-branch-border-last" : ""}
                isLastChild={isLast}
                expandedNodes={expandedNodes}
                isSelectableNode={isSelectableNode}
                scrollToSelected={scrollToSelected}
                getNodeIconByType={getNodeIconByType}
                getNodeActions={getNodeActions}
                previewMode={previewMode}
                getRightComponent={getRightComponent}
                getExtendedTitle={getExtendedTitle}
                tooltipContent={tooltipContent}
                readOnly={readOnly}
                warningTooltip={warningTooltip}
                getActionTooltips={getActionTooltips}
                isStatic={isStatic}
                isHighlightedNode={isHighlightedNode}
              />
            </React.Fragment>
          );
        })}
        {remainingCount > 0 && (
          <div
            className={`tree-node-show-more tree-node-level-${level + 1}`}
            data-testid={"tree-show-more"}
          >
            <TextButton
              buttonStyle={"textLink"}
              size={"small"}
              label={getShowMoreLabel(remainingCount)}
              data-testid={"tree-show-more"}
              onClick={() =>
                setVisibleChildren({
                  count: visibleChildrenCount + CHILDREN_PAGE_SIZE,
                  searchValue,
                })
              }
            />
          </div>
        )}
      </>
    );
  };

  const selectNode = () => {
    if (isSelectableNode(nodeData) && !!onSelect) {
      const node = { ...nodeData, level };
      onSelect(node);
    }
  };

  useEffect(() => {
    if (
      scrollToSelected &&
      type !== resourceTypes.ROOT &&
      !!selectedRef?.current
    ) {
      selectedRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
    }
  }, [scrollToSelected, selectedRef, type]);

  const getIconElementByType = (type?: string, id?: string) => {
    if (!getNodeIconByType) {
      return (
        !!getIconByType(type ?? "") && (
          <div className={"tree-node-label-container-icon-wrap"}>
            <i
              className={`tree-node-icon seem-icon ${getIconByType(type ?? "")}`}
            />
          </div>
        )
      );
    } else {
      return (
        !!getNodeIconByType(type, id) && (
          <div className={"tree-node-label-container-icon-wrap"}>
            {getNodeIconByType(type, id)}
          </div>
        )
      );
    }
  };

  return (
    <div
      className={`tree-node-wrapper ${
        selectedNode === id ? "is-selected" : ""
      } ${readOnly ? "is-readOnly" : ""}`}
      key={id}
      data-haschildren={!!children?.length}
      ref={selectedNode === id ? selectedRef : null}
    >
      {isHiddenRoot && type === resourceTypes.ROOT ? (
        <></>
      ) : (
        <div
          className={`tree-node-wrapper-data-${level}`}
          data-testid={`tree-node-container-level-${level}`}
        >
          <div
            className={`tree-node tree-node-level-${level} ${classNameWrapper} ${
              selectedNode === id ? "is-selected" : ""
            }`}
          >
            {withCheckbox && isSelectableNode(nodeData) && (
              <CheckBox
                data-testid={`tree-node-${id}`}
                value={nodeData}
                checked={isNodeChecked(id)}
                indeterminate={isNodeIndeterminate(id)}
                name={`tree-node-${id}`}
                onChange={(node) =>
                  nodeOperations.onCheck?.(node, checkedNodes)
                }
                hideLabelHelper
              />
            )}
            <div
              className={`expandable-indicator ${
                showExpandToggle ? "" : "is-expandable-hidden"
              }`}
              onClick={() => {
                if (showExpandToggle) {
                  if (expand) {
                    // Collapsing drops the paging, so re-expanding starts over.
                    setVisibleChildren(null);
                  }
                  setManualExpand({ expanded: !expand, searchValue });
                } else {
                  selectNode();
                }
              }}
            >
              {showExpandToggle && (
                <i
                  className={`seem-icon seem-icon-chevron-down ${
                    !expand ? "is-collapsed" : ""
                  }`}
                />
              )}
            </div>

            <div
              className={`tree-node-data ${
                type === resourceTypes.ROOT ? "is-root" : type
              } ${
                // A static view has no selection state, so it takes neither style.
                isStatic
                  ? ""
                  : isSelectableNode(nodeData)
                    ? "selectable"
                    : "not-selectable"
              }`}
              data-testid={dataTestId}
            >
              <div
                className={`tree-node-label-container ${
                  !withCheckbox || !isSelectableNode(nodeData)
                    ? "has-left-icon"
                    : "no-icon"
                } ${hasWarning(id) ? "has-right-icon" : ""} ${
                  editableNodes && nodeData?.editable ? "is-editable" : ""
                }`}
                onClick={() => {
                  if (withCheckbox && isSelectableNode(nodeData)) {
                    nodeOperations.onCheck?.(nodeData, checkedNodes);
                  } else {
                    selectNode();
                  }
                }}
              >
                {getIconElementByType(type, id)}
                <Tooltip content={tooltipContent?.(nodeData) || name}>
                  <div className={`tree-node-label`} data-testid={name}>
                    <Highlighter
                      highlightClassName="highlight-text-wrap"
                      searchWords={[searchValue ?? ""]}
                      autoEscape={true}
                      textToHighlight={`${name}`}
                    />
                  </div>
                </Tooltip>
                {!!getExtendedTitle && getExtendedTitle(nodeData)}
                {hasWarning(id) &&
                  (warningTooltip ? (
                    <Tooltip content={warningTooltip}>{warningIcon}</Tooltip>
                  ) : (
                    warningIcon
                  ))}
              </div>
              {!!nodeActions && (
                <div className={"tree-node-leading-actions"}>{nodeActions}</div>
              )}
              {canEditNode && (
                <div className="form-button-group tree-node-action-buttons-wrap">
                  <div>
                    <IconButton
                      buttonStyle={"secondary"}
                      withTooltip={{
                        content: actionTooltips.edit ?? "Edit Details",
                      }}
                      data-testid={"edit-scope-via-list"}
                      wrapperClassName={"tree-node-action"}
                      className={"filter-panel-option-edit"}
                      isIcon={"seem-icon-edit"}
                      onClick={() => onEdit?.(nodeData)}
                    />
                  </div>

                  {!!onMove && (
                    <div>
                      <IconButton
                        buttonStyle={"secondary"}
                        withTooltip={{ content: actionTooltips.move ?? "Move" }}
                        data-testid={"move-scope-via-list"}
                        wrapperClassName={"tree-node-action"}
                        className={"filter-panel-option-move"}
                        isIcon={"seem-icon-hierarchy"}
                        onClick={() => onMove(nodeData)}
                      />
                    </div>
                  )}

                  <div>
                    <IconButton
                      buttonStyle={"secondary"}
                      withTooltip={{ content: "Delete" }}
                      data-testid={"delete-scope-via-list"}
                      wrapperClassName={"tree-node-action"}
                      className={"tree-node-delete filter-panel-option-delete"}
                      isIcon={"seem-icon-bin"}
                      onClick={() => onDelete?.(nodeData)}
                    />
                  </div>
                </div>
              )}
              {!!getRightComponent && getRightComponent(nodeData)}
            </div>
          </div>
        </div>
      )}
      {expand && <div className="tree-node-children">{renderChildren()}</div>}
    </div>
  );
};

export default TreeNode;
